import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const COUNTDOWN_START = 10;

export default function Disconnected() {
  const navigate   = useNavigate();
  const [count, setCount]     = useState(COUNTDOWN_START);
  const [paused, setPaused]   = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (cancelled) return;

    intervalRef.current = setInterval(() => {
      if (!paused) {
        setCount((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            navigate('/connect?reconnect=true');
            return 0;
          }
          return prev - 1;
        });
      }
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [paused, cancelled, navigate]);

  function handleButtonClick(path) {
    setCancelled(true);
    clearInterval(intervalRef.current);
    navigate(path);
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* Icon */}
        <div style={styles.iconWrap}>⚡</div>

        {/* Eyebrow */}
        <p style={styles.eyebrow}>QuickBooks Connection</p>

        {/* Heading */}
        <h1 style={styles.heading}>Disconnected</h1>

        {/* Body */}
        <p style={styles.body}>
          Your QuickBooks Online connection has been removed from Acct4Cast.
          Your data remains intact. Reconnect anytime to resume live sync.
        </p>

        {/* Status pills */}
        <div style={styles.pillRow}>
          <span style={{ ...styles.pill, ...styles.pillGreen }}>Data preserved</span>
          <span style={{ ...styles.pill, ...styles.pillAmber }}>Sync paused</span>
        </div>

        {/* Countdown */}
        {!cancelled && (
          <p style={styles.countdown}>
            Redirecting to reconnect in {count}s...
          </p>
        )}

        {/* Buttons */}
        <div style={styles.buttonCol}>
          <button
            style={styles.ctaPrimary}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onClick={() => handleButtonClick('/connect?reconnect=true')}
          >
            Reconnect QuickBooks →
          </button>
          <button
            style={styles.ctaSecondary}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onClick={() => handleButtonClick('/dashboard')}
          >
            Return to Dashboard
          </button>
        </div>
      </div>

      {/* Support link */}
      <p style={styles.support}>
        Need help?{' '}
        <a href="mailto:support@acct4cast.com" style={styles.supportLink}>
          Contact support@acct4cast.com
        </a>
      </p>
    </div>
  );
}

const styles = {
  page: {
    minHeight:       '100vh',
    backgroundColor: '#0C1A36',
    display:         'flex',
    flexDirection:   'column',
    alignItems:      'center',
    justifyContent:  'center',
    padding:         '2rem 1rem',
    animation:       'fadeUp 0.5s ease both',
  },
  card: {
    width:           '100%',
    maxWidth:        '440px',
    backgroundColor: 'rgba(17,34,64,0.9)',
    border:          '1px solid rgba(0,168,204,0.25)',
    borderRadius:    '16px',
    padding:         '2.5rem',
    boxShadow:       '0 8px 40px rgba(0,0,0,0.4)',
    textAlign:       'center',
  },
  iconWrap: {
    fontSize:     '3rem',
    color:        '#C8A000',
    marginBottom: '0.75rem',
    lineHeight:   1,
  },
  eyebrow: {
    fontFamily:    "'DM Mono', monospace",
    color:         '#00A8CC',
    fontSize:      '0.72rem',
    fontWeight:    500,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    margin:        '0 0 0.6rem',
  },
  heading: {
    fontFamily:  "'Sora', sans-serif",
    fontWeight:  800,
    fontSize:    '1.75rem',
    color:       '#EDF1FF',
    margin:      '0 0 1rem',
    lineHeight:  1.2,
  },
  body: {
    fontFamily:  "'Sora', sans-serif",
    color:       '#7A90B8',
    fontSize:    '0.9rem',
    lineHeight:  1.65,
    margin:      '0 0 1.25rem',
  },
  pillRow: {
    display:        'flex',
    justifyContent: 'center',
    gap:            '0.75rem',
    marginBottom:   '1rem',
  },
  pill: {
    fontFamily:    "'DM Mono', monospace",
    fontSize:      '0.72rem',
    fontWeight:    500,
    padding:       '0.3rem 0.75rem',
    borderRadius:  '999px',
    letterSpacing: '0.04em',
  },
  pillGreen: {
    backgroundColor: 'rgba(61,170,74,0.15)',
    color:           '#3DAA4A',
    border:          '1px solid rgba(61,170,74,0.35)',
  },
  pillAmber: {
    backgroundColor: 'rgba(200,160,0,0.15)',
    color:           '#C8A000',
    border:          '1px solid rgba(200,160,0,0.35)',
  },
  countdown: {
    fontFamily:   "'DM Mono', monospace",
    color:        '#00A8CC',
    fontSize:     '0.8rem',
    margin:       '0 0 1.25rem',
  },
  buttonCol: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '0.65rem',
  },
  ctaPrimary: {
    width:        '100%',
    padding:      '0.85rem 1.5rem',
    background:   'linear-gradient(135deg, #00A8CC 0%, #3DAA4A 100%)',
    border:       'none',
    borderRadius: '10px',
    color:        '#fff',
    fontFamily:   "'Sora', sans-serif",
    fontWeight:   700,
    fontSize:     '1rem',
    cursor:       'pointer',
    transition:   'opacity 0.2s',
  },
  ctaSecondary: {
    width:           '100%',
    padding:         '0.85rem 1.5rem',
    backgroundColor: 'transparent',
    border:          '1px solid rgba(0,168,204,0.35)',
    borderRadius:    '10px',
    color:           '#7A90B8',
    fontFamily:      "'Sora', sans-serif",
    fontWeight:      600,
    fontSize:        '0.95rem',
    cursor:          'pointer',
    transition:      'border-color 0.2s, color 0.2s',
  },
  support: {
    marginTop:   '1.25rem',
    color:       '#7A90B8',
    fontSize:    '0.75rem',
    fontFamily:  "'Sora', sans-serif",
  },
  supportLink: {
    color:          '#00A8CC',
    textDecoration: 'none',
  },
};
